<template>
  <section
    class="proctor-monitor"
    :class="[`proctor-monitor--${mode}`, `proctor-monitor--${faceTone}`]"
    :aria-label="text('proctorMonitorTitle')"
  >
    <div class="proctor-monitor__camera">
      <video
        ref="video"
        class="proctor-monitor__video"
        autoplay
        muted
        playsinline
      ></video>
      <div
        class="proctor-monitor__face-guide"
        aria-hidden="true"
      ></div>
      <div class="proctor-monitor__live">
        <span class="proctor-monitor__live-dot"></span>
        {{ text("proctorLive") }}
      </div>
    </div>

    <div class="proctor-monitor__signals">
      <div class="proctor-monitor__heading">
        <span>{{ text("proctorMonitorTitle") }}</span>
        <span v-if="mode === 'ready'" class="proctor-monitor__scope">
          {{ text("proctorReadyScope") }}
        </span>
      </div>

      <canvas
        ref="waveform"
        class="proctor-monitor__waveform"
        width="260"
        height="44"
        role="img"
        :aria-label="text('proctorWaveformLabel')"
      ></canvas>

      <div class="proctor-monitor__status-list" aria-live="polite">
        <div class="proctor-monitor__status">
          <span class="proctor-monitor__dot" :class="`is-${faceTone}`"></span>
          <span>{{ faceStatusText }}</span>
          <span v-if="faceConfidence > 0" class="proctor-monitor__metric">
            {{ Math.round(faceConfidence * 100) }}%
          </span>
        </div>
        <div class="proctor-monitor__status">
          <span class="proctor-monitor__dot" :class="`is-${speechTone}`"></span>
          <span class="proctor-monitor__status-copy">
            <span>{{ speechStatusText }}</span>
            <small v-if="speechState === 'error' && speechErrorDetail">
              {{ speechErrorDetail }}
            </small>
          </span>
          <span v-if="speechSavedCount > 0" class="proctor-monitor__metric">
            {{ speechSavedCount }} {{ text("proctorSaved") }}
          </span>
        </div>
      </div>
    </div>

    <div
      v-if="mode === 'exam' && faceBlocked"
      class="proctor-monitor__blocker"
      role="alertdialog"
      aria-modal="true"
      :aria-label="text('proctorFaceBlockedTitle')"
    >
      <div class="proctor-monitor__blocker-panel">
        <div class="proctor-monitor__blocker-mark" aria-hidden="true">
          <i class="el-icon-camera"></i>
        </div>
        <div class="proctor-monitor__blocker-copy">
          <div class="proctor-monitor__eyebrow">
            {{ text("proctorAttentionRequired") }}
          </div>
          <h2>{{ text("proctorFaceBlockedTitle") }}</h2>
          <p>{{ faceBlockedDescription }}</p>
          <div class="proctor-monitor__recovery">
            <span class="proctor-monitor__recovery-dot"></span>
            {{ text("proctorFaceRecovery") }}
          </div>
        </div>
        <div class="proctor-monitor__blocker-video-wrap">
          <video
            ref="blockedVideo"
            class="proctor-monitor__blocker-video"
            autoplay
            muted
            playsinline
          ></video>
          <div
            class="proctor-monitor__face-guide"
            aria-hidden="true"
          ></div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { FaceDetector, FilesetResolver } from "@mediapipe/tasks-vision";
import * as qiniu from "qiniu-js";
import {
  getExamEvidenceQiniuToken,
  saveProctorEvent,
} from "../../api/index";
import { getProjectCode, getUserId } from "../../utils/store";

const AI_ASSET_BASE =
  process.env.VUE_APP_AI_ASSET_BASE ||
  "https://mainbucket.learningfirst.cn/remote-exam-ai/20260728-v2/";
const FACE_INTERVAL_MS = 500;
const FACE_CONFIDENCE_MIN = 0.5;
// Only distinct camera frames count toward the 6-failure / 3-success rules.
const INVALID_FACE_SAMPLES_TO_BLOCK = 6;
const VALID_FACE_SAMPLES_TO_CONFIRM = 3;
const MIN_SPEECH_LOG_MS = 500;
const SPEECH_REDEMPTION_MS = 1500;
const FACE_SNAPSHOT_MAX_WIDTH = 640;
const FACE_SNAPSHOT_QUALITY = 0.72;
const MEDIAPIPE_FEEDBACK_WARNING =
  "Feedback manager requires a model with a single signature inference.";

const COPY = {
  proctorMonitorTitle: "Camera & audio monitor",
  proctorReadyScope: "Preview only",
  proctorLive: "Live",
  proctorWaveformLabel: "Live microphone level waveform",
  proctorFaceLoading: "Loading face detection…",
  proctorFaceHoldStill: "Hold still while your face is verified",
  proctorFaceValid: "Face detected",
  proctorFaceMissing: "No face detected",
  proctorFaceMultiple: "More than one face detected",
  proctorFaceUnavailable: "Face detection unavailable",
  proctorSpeechLoading: "Loading speech detection…",
  proctorSpeechQuiet: "Microphone ready — no speech",
  proctorSpeechDetected: "Speech detected",
  proctorSpeechSaving: "Saving speech event…",
  proctorSpeechSaveFailed: "Speech event could not be saved",
  proctorSpeechNoTrack: "Microphone is not connected — reload and run the device check again",
  proctorSpeechPermissionBlocked: "Microphone access is blocked in the browser",
  proctorSpeechDetectorFailed: "Microphone is connected, but the speech detector failed to load",
  proctorSpeechUnavailable: "Microphone speech detection unavailable",
  proctorSaved: "saved",
  proctorAttentionRequired: "Attention required",
  proctorFaceBlockedTitle: "Your exam is temporarily covered",
  proctorFaceBlockedMissing: "No face has been detected for several seconds. Return to the camera view to continue.",
  proctorFaceBlockedMultiple: "More than one face is visible. Ask every other person to leave the camera view.",
  proctorFaceBlockedUnavailable: "The face detector is unavailable. Keep the camera connected; the page will recover automatically when detection resumes.",
  proctorFaceRecovery: "This message closes automatically after your face is verified.",
};

function filterNonActionableMediaPipeWarning() {
  const scope = typeof window === "undefined" ? null : window;
  const debugLogger = scope && scope.custom_dbg;
  if (
    typeof debugLogger !== "function" ||
    debugLogger.__remoteExamWarningFilter
  ) {
    return;
  }

  const filteredDebugLogger = (...args) => {
    const message = args.length ? String(args[0]) : "";
    if (message.includes(MEDIAPIPE_FEEDBACK_WARNING)) return;
    debugLogger(...args);
  };
  filteredDebugLogger.__remoteExamWarningFilter = true;
  scope.custom_dbg = filteredDebugLogger;
}

export default {
  name: "ProctorMonitor",
  props: {
    stream: {
      default: null,
      validator(value) {
        return value === null || (
          typeof value === "object" &&
          typeof value.getTracks === "function"
        );
      },
    },
    templateId: {
      type: String,
      default: "",
    },
    projectCode: {
      type: String,
      default: "",
    },
    mode: {
      type: String,
      default: "ready",
      validator(value) {
        return ["ready", "exam"].includes(value);
      },
    },
    logSpeech: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    resolvedProjectCode() {
      return String(this.projectCode || getProjectCode() || "").trim();
    },
  },
  data() {
    return {
      faceDetector: null,
      faceTimer: null,
      faceState: "loading",
      faceConfidence: 0,
      faceCount: 0,
      faceValid: false,
      faceBlocked: false,
      validFaceSamples: 0,
      invalidFaceSamples: 0,
      lastInvalidFaceState: "",
      faceMissingCandidateAt: 0,
      faceMissingStartedAt: 0,
      faceMissingConfirmedAt: 0,
      faceMissingEventId: "",
      faceEventSequence: 0,
      faceLogQueue: [],
      faceLogSending: false,
      detectorBusy: false,
      lastFaceVideoTime: -1,
      vad: null,
      vadLoading: true,
      speechState: "loading",
      speechErrorReason: "",
      speechErrorDetail: "",
      speechProbability: 0,
      maxSpeechProbability: 0,
      speechSavedCount: 0,
      speechStartedAt: 0,
      speechConfirmed: false,
      speechLogState: "idle",
      speechEventSequence: 0,
      speechLogQueue: [],
      speechLogSending: false,
      waveSamples: Array(42).fill(0.03),
      lastWaveDrawAt: 0,
      monitorGeneration: 0,
    };
  },
  computed: {
    faceTone() {
      if (this.faceState === "valid" && this.faceValid) return "ok";
      if (this.faceState === "loading") return "loading";
      if (this.faceState === "error") return "error";
      return "warning";
    },
    speechTone() {
      if (this.speechState === "speaking") return "warning";
      if (this.speechLogState === "error") return "error";
      if (this.speechLogState === "saving") return "loading";
      if (this.speechState === "error") return "error";
      if (this.speechState === "loading") return "loading";
      return "ok";
    },
    faceStatusText() {
      const statusKeys = {
        loading: "m.proctorFaceLoading",
        valid: this.faceValid
          ? "m.proctorFaceValid"
          : "m.proctorFaceHoldStill",
        missing: "m.proctorFaceMissing",
        multiple: "m.proctorFaceMultiple",
        error: "m.proctorFaceUnavailable",
      };
      return this.text(statusKeys[this.faceState] || statusKeys.loading);
    },
    speechStatusText() {
      if (this.speechState === "speaking") {
        return this.text("proctorSpeechDetected");
      }
      if (this.speechLogState === "saving") {
        return this.text("proctorSpeechSaving");
      }
      if (this.speechLogState === "error") {
        return this.text("proctorSpeechSaveFailed");
      }
      const statusKeys = {
        loading: "m.proctorSpeechLoading",
        quiet: "m.proctorSpeechQuiet",
        error: {
          no_track: "m.proctorSpeechNoTrack",
          permission: "m.proctorSpeechPermissionBlocked",
          detector: "m.proctorSpeechDetectorFailed",
        }[this.speechErrorReason] || "m.proctorSpeechUnavailable",
      };
      return this.text(statusKeys[this.speechState] || statusKeys.loading);
    },
    faceBlockedDescription() {
      const statusKeys = {
        missing: "m.proctorFaceBlockedMissing",
        multiple: "m.proctorFaceBlockedMultiple",
        error: "m.proctorFaceBlockedUnavailable",
      };
      return this.text(
        statusKeys[this.faceState] || "proctorFaceBlockedMissing"
      );
    },
  },
  watch: {
    stream: {
      immediate: true,
      handler(stream) {
        this.monitorGeneration += 1;
        const generation = this.monitorGeneration;
        if (!stream) {
          this.stopMonitoring();
          return;
        }
        this.$nextTick(() => {
          this.startMonitoring(stream, generation);
        });
      },
    },
    faceBlocked(blocked) {
      if (!blocked) return;
      this.$nextTick(() => this.attachStreamToVideo(this.$refs.blockedVideo));
    },
  },
  beforeDestroy() {
    this.finishFaceMissingIncident(Date.now());
    this.monitorGeneration += 1;
    this.stopMonitoring();
  },
  methods: {
    text(key) {
      return COPY[String(key || "").replace(/^m\./, "")] || key;
    },
    assetPath(path) {
      return new URL(path, AI_ASSET_BASE).href;
    },
    async startMonitoring(stream, generation) {
      await this.stopMonitoring(false);
      if (generation !== this.monitorGeneration || !stream) return;

      this.resetMonitorState();
      this.attachStreamToVideo(this.$refs.video);

      const results = await Promise.allSettled([
        this.initFaceDetector(generation),
        this.initVad(stream, generation),
      ]);

      if (results[0].status === "rejected") {
        console.error("Face detector initialization failed", results[0].reason);
        this.faceState = "error";
        this.confirmInvalidFaceState();
      }
      if (results[1].status === "rejected") {
        console.error("Voice activity detector initialization failed", results[1].reason);
        this.vadLoading = false;
        this.speechErrorReason = this.getVadErrorReason(results[1].reason);
        this.speechErrorDetail = this.getVadErrorDetail(results[1].reason);
        this.speechState = "error";
      }
    },
    resetMonitorState() {
      this.faceState = "loading";
      this.faceConfidence = 0;
      this.faceCount = 0;
      this.faceValid = false;
      this.faceBlocked = false;
      this.validFaceSamples = 0;
      this.invalidFaceSamples = 0;
      this.lastInvalidFaceState = "";
      this.faceMissingCandidateAt = 0;
      this.lastFaceVideoTime = -1;
      this.vadLoading = true;
      this.speechState = "loading";
      this.speechErrorReason = "";
      this.speechErrorDetail = "";
      this.speechProbability = 0;
      this.maxSpeechProbability = 0;
      this.speechStartedAt = 0;
      this.speechConfirmed = false;
      if (!this.speechLogSending) this.speechLogState = "idle";
      this.waveSamples = Array(42).fill(0.03);
      this.drawWaveform();
      this.emitFaceStatus();
    },
    attachStreamToVideo(video) {
      if (!video || !this.stream) return;
      if (video.srcObject !== this.stream) {
        video.srcObject = this.stream;
      }
      const playPromise = video.play();
      if (playPromise && playPromise.catch) {
        playPromise.catch(() => {});
      }
    },
    async initFaceDetector(generation) {
      const vision = await FilesetResolver.forVisionTasks(
        this.assetPath("mediapipe/wasm")
      );
      if (generation !== this.monitorGeneration) return;
      filterNonActionableMediaPipeWarning();

      this.faceDetector = await FaceDetector.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: this.assetPath(
            "mediapipe/models/blaze_face_short_range.tflite"
          ),
        },
        runningMode: "VIDEO",
        minDetectionConfidence: FACE_CONFIDENCE_MIN,
        minSuppressionThreshold: 0.3,
      });

      if (generation !== this.monitorGeneration) return;
      this.scheduleFaceDetection(generation, 0);
    },
    scheduleFaceDetection(generation, delay) {
      if (this.faceTimer) window.clearTimeout(this.faceTimer);
      this.faceTimer = window.setTimeout(async () => {
        await this.detectFaceFrame(generation);
        if (generation === this.monitorGeneration && this.faceDetector) {
          this.scheduleFaceDetection(generation, FACE_INTERVAL_MS);
        }
      }, delay);
    },
    async detectFaceFrame(generation) {
      if (
        this.detectorBusy ||
        document.hidden ||
        generation !== this.monitorGeneration
      ) {
        return;
      }
      const video = this.$refs.video;
      if (!video || video.readyState < 2 || !video.videoWidth) return;

      const videoTime = Number(video.currentTime);
      // A stalled MediaStream can expose the same decoded frame repeatedly.
      // Do not let one failed frame count as several consecutive failures.
      if (
        !Number.isFinite(videoTime) ||
        videoTime <= this.lastFaceVideoTime
      ) {
        return;
      }
      this.lastFaceVideoTime = videoTime;

      this.detectorBusy = true;
      try {
        const result = this.faceDetector.detectForVideo(
          video,
          videoTime * 1000
        );
        this.applyFaceResult(result);
      } catch (error) {
        console.error("Face frame detection failed", error);
        this.faceState = "error";
        this.confirmInvalidFaceState();
      } finally {
        this.detectorBusy = false;
      }
    },
    applyFaceResult(result) {
      const detections = result && result.detections ? result.detections : [];

      if (detections.length === 0) {
        this.applyRawFaceState("missing", false, 0, 0);
        return;
      }
      const confidence = Math.max(
        ...detections.map((item) => this.getDetectionConfidence(item))
      );
      const valid = confidence >= FACE_CONFIDENCE_MIN;
      this.applyRawFaceState(
        valid ? "valid" : "missing",
        valid,
        detections.length,
        confidence
      );
    },
    getDetectionConfidence(detection) {
      const categories = detection && detection.categories;
      return categories && categories[0] && Number(categories[0].score)
        ? Number(categories[0].score)
        : 0;
    },
    applyRawFaceState(state, valid, faceCount, confidence) {
      if (valid) {
        this.faceState = "valid";
        this.faceCount = faceCount;
        this.faceConfidence = confidence;
        this.validFaceSamples += 1;
        this.invalidFaceSamples = 0;
        this.lastInvalidFaceState = "";
        this.faceMissingCandidateAt = 0;
        if (this.validFaceSamples >= VALID_FACE_SAMPLES_TO_CONFIRM) {
          const changed = !this.faceValid || this.faceBlocked;
          this.faceValid = true;
          this.faceBlocked = false;
          this.finishFaceMissingIncident(Date.now());
          if (changed) this.emitFaceStatus();
        }
        return;
      }

      if (state !== this.lastInvalidFaceState) {
        this.invalidFaceSamples = 0;
        this.lastInvalidFaceState = state;
        this.faceMissingCandidateAt = state === "missing" ? Date.now() : 0;
      }
      if (state !== "missing") {
        this.finishFaceMissingIncident(Date.now());
      }
      this.invalidFaceSamples += 1;
      this.validFaceSamples = 0;
      if (this.invalidFaceSamples >= INVALID_FACE_SAMPLES_TO_BLOCK) {
        this.confirmInvalidFaceState(state, faceCount, confidence);
      }
    },
    confirmInvalidFaceState(
      state = this.faceState,
      faceCount = this.faceCount,
      confidence = this.faceConfidence
    ) {
      const previousState = this.faceState;
      const wasValid = this.faceValid;
      const wasBlocked = this.faceBlocked;
      this.faceState = state;
      this.faceCount = faceCount;
      this.faceConfidence = confidence;
      this.faceValid = false;
      if (this.mode === "exam") this.faceBlocked = true;
      if (state === "missing") {
        this.startFaceMissingIncident(
          this.faceMissingCandidateAt || Date.now()
        );
      }
      const changed =
        wasValid !== this.faceValid ||
        wasBlocked !== this.faceBlocked ||
        previousState !== this.faceState;
      if (changed) this.emitFaceStatus();
    },
    emitFaceStatus() {
      this.$emit("face-status-change", {
        valid: this.faceValid,
        status: this.faceState,
        faceCount: this.faceCount,
        confidence: this.faceConfidence,
      });
    },
    startFaceMissingIncident(startedAt) {
      if (this.mode !== "exam" || this.faceMissingEventId) return;

      const confirmedAt = Date.now();
      this.faceEventSequence += 1;
      this.faceMissingStartedAt = startedAt;
      this.faceMissingConfirmedAt = confirmedAt;
      this.faceMissingEventId = `face-missing-${startedAt}-${this.faceEventSequence}-${Math.random()
        .toString(36)
        .slice(2, 10)}`;
      const snapshotContext = {
        clientEventId: this.faceMissingEventId,
        startedAt: this.faceMissingStartedAt,
        confirmedAt: this.faceMissingConfirmedAt,
      };
      this.queueFaceMissingLog("started", confirmedAt);
      this.captureAndUploadFaceMissingSnapshot(snapshotContext).catch(
        (error) => {
          console.warn("Face-missing evidence capture failed", error);
        }
      );
    },
    finishFaceMissingIncident(endedAt) {
      if (!this.faceMissingEventId) return;
      this.queueFaceMissingLog("ended", endedAt);
      this.faceMissingStartedAt = 0;
      this.faceMissingConfirmedAt = 0;
      this.faceMissingEventId = "";
    },
    queueFaceMissingLog(eventPhase, endedAt) {
      const startedAt = this.faceMissingStartedAt;
      if (!startedAt || !this.faceMissingEventId) return;

      const safeEndedAt = Math.max(startedAt, endedAt);
      this.faceLogQueue.push({
        event_type: "face_missing",
        event_phase: eventPhase,
        student_id: getUserId(),
        project_code: this.resolvedProjectCode,
        template_id: this.templateId,
        start_time_ms: startedAt,
        confirmed_time_ms: this.faceMissingConfirmedAt,
        end_time_ms: safeEndedAt,
        duration_ms: safeEndedAt - startedAt,
        confidence: 0,
        face_count: 0,
        client_timezone_offset: new Date().getTimezoneOffset(),
        client_event_id: this.faceMissingEventId,
        detector_version: "mediapipe-blaze-face-browser",
        _attempts: 0,
      });
      this.flushFaceLogs();
    },
    flushFaceLogs() {
      if (
        this.faceLogSending ||
        this.faceLogQueue.length === 0 ||
        navigator.onLine === false
      ) {
        return;
      }

      const event = this.faceLogQueue[0];
      event._attempts = (event._attempts || 0) + 1;
      const payload = { ...event };
      delete payload._attempts;
      let removeEvent = false;
      this.faceLogSending = true;
      saveProctorEvent(payload, {
        timeout: 8000,
        skipNetworkErrorMessage: true,
      })
        .then((response) => {
          const result = response && response.data ? response.data : null;
          if (!result || result.code !== 0) {
            throw new Error(
              result && result.msg
                ? result.msg
                : "Face-missing event was rejected"
            );
          }
          this.emitPenaltySummary(result);
          removeEvent = true;
        })
        .catch((error) => {
          console.warn("Face-missing event save failed", error);
          removeEvent = event._attempts >= 3;
        })
        .finally(() => {
          if (removeEvent && this.faceLogQueue[0] === event) {
            this.faceLogQueue.shift();
          }
          this.faceLogSending = false;
          if (this.faceLogQueue.length > 0) {
            window.setTimeout(() => this.flushFaceLogs(), 700);
          }
        });
    },
    captureFaceMissingSnapshotBlob() {
      const video = this.$refs.video;
      if (!video || video.readyState < 2 || !video.videoWidth) {
        return Promise.resolve(null);
      }

      const scale = Math.min(1, FACE_SNAPSHOT_MAX_WIDTH / video.videoWidth);
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
      canvas.height = Math.max(1, Math.round(video.videoHeight * scale));
      const context = canvas.getContext("2d");
      if (!context) return Promise.resolve(null);

      // drawImage happens synchronously, so this is exactly the sixth failed frame.
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      return new Promise((resolve) => {
        canvas.toBlob(
          (blob) => resolve(blob),
          "image/jpeg",
          FACE_SNAPSHOT_QUALITY
        );
      });
    },
    async captureAndUploadFaceMissingSnapshot(snapshotContext) {
      const snapshotBlob = await this.captureFaceMissingSnapshotBlob();
      if (!snapshotBlob) {
        console.warn("Unable to capture the face-missing evidence frame");
        return;
      }

      let lastError = null;
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const tokenResponse = await getExamEvidenceQiniuToken(
            "camera",
            this.resolvedProjectCode,
            this.templateId,
            getUserId(),
            {
              timeout: 8000,
              skipNetworkErrorMessage: true,
            }
          );
          const tokenData =
            tokenResponse && tokenResponse.data && tokenResponse.data.data;
          if (
            !tokenResponse ||
            !tokenResponse.data ||
            tokenResponse.data.code !== 0 ||
            !tokenData ||
            !tokenData.token ||
            !tokenData.key_prefix
          ) {
            throw new Error("Unable to obtain the evidence upload token");
          }

          const suffix = Math.random().toString(36).slice(2, 10);
          const objectKey = `${tokenData.key_prefix}face-missing-${snapshotContext.confirmedAt}-${suffix}.jpg`;
          const uploadResult = await new Promise((resolve, reject) => {
            qiniu.upload(snapshotBlob, objectKey, tokenData.token).subscribe({
              error: reject,
              complete: resolve,
            });
          });
          const uploadedKey =
            uploadResult && uploadResult.key ? uploadResult.key : objectKey;
          this.queueFaceMissingSnapshotLog(snapshotContext, uploadedKey);
          return;
        } catch (error) {
          lastError = error;
        }
      }
      console.warn("Face-missing evidence upload failed", lastError);
    },
    queueFaceMissingSnapshotLog(snapshotContext, snapshotKey) {
      const endedAt = snapshotContext.confirmedAt;
      this.faceLogQueue.push({
        event_type: "face_missing",
        event_phase: "snapshot",
        student_id: getUserId(),
        project_code: this.resolvedProjectCode,
        template_id: this.templateId,
        start_time_ms: snapshotContext.startedAt,
        confirmed_time_ms: snapshotContext.confirmedAt,
        end_time_ms: endedAt,
        duration_ms: Math.max(0, endedAt - snapshotContext.startedAt),
        confidence: 0,
        face_count: 0,
        snapshot_key: snapshotKey,
        client_timezone_offset: new Date().getTimezoneOffset(),
        client_event_id: snapshotContext.clientEventId,
        detector_version: "mediapipe-blaze-face-browser",
        _attempts: 0,
      });
      this.flushFaceLogs();
    },
    async initVad(stream, generation) {
      const audioTracks = stream.getAudioTracks ? stream.getAudioTracks() : [];
      const liveAudioTracks = audioTracks.filter(
        (track) => track && track.readyState !== "ended"
      );
      if (!liveAudioTracks.length) {
        const error = new Error("No live microphone track is available");
        error.code = "VAD_NO_AUDIO_TRACK";
        throw error;
      }

      const micVad = window.vad && window.vad.MicVAD;
      if (!micVad) {
        throw new Error("VAD browser runtime failed to load");
      }
      this.vad = await micVad.new({
        model: "v5",
        startOnLoad: true,
        processorType: "ScriptProcessor",
        baseAssetPath: this.assetPath("vad/"),
        onnxWASMBasePath: this.assetPath("vad/"),
        positiveSpeechThreshold: 0.72,
        negativeSpeechThreshold: 0.45,
        redemptionMs: SPEECH_REDEMPTION_MS,
        preSpeechPadMs: 320,
        minSpeechMs: MIN_SPEECH_LOG_MS,
        getStream: async () => stream,
        pauseStream: async () => {},
        resumeStream: async () => stream,
        ortConfig: (ort) => {
          ort.env.logLevel = "error";
          ort.env.wasm.numThreads = 1;
          // Older Nginx installations commonly serve `.mjs` as
          // application/octet-stream, which browsers reject for dynamic
          // imports. Point ONNX Runtime at the identical `.js` copy so the
          // detector works without requiring a server MIME override.
          ort.env.wasm.wasmPaths = {
            mjs: this.assetPath("vad/ort-wasm-simd-threaded.js"),
            wasm: this.assetPath("vad/ort-wasm-simd-threaded.wasm"),
          };
        },
        onFrameProcessed: (probabilities, frame) => {
          this.handleAudioFrame(probabilities, frame);
        },
        onSpeechStart: () => this.handleSpeechStart(),
        onSpeechRealStart: () => this.handleSpeechRealStart(),
        onVADMisfire: () => this.handleSpeechMisfire(),
        onSpeechEnd: () => this.handleSpeechEnd(),
      });

      if (generation !== this.monitorGeneration) {
        await this.destroyVad();
        return;
      }
      this.vadLoading = false;
      this.speechErrorReason = "";
      this.speechErrorDetail = "";
      this.speechState = "quiet";
    },
    getVadErrorDetail(error) {
      const message = String(
        error && error.message ? error.message : error || "Unknown VAD error"
      )
        .replace(/\s+/g, " ")
        .trim();
      return message.length > 180 ? `${message.slice(0, 177)}...` : message;
    },
    getVadErrorReason(error) {
      if (error && error.code === "VAD_NO_AUDIO_TRACK") return "no_track";
      const message = String(
        error && error.message ? error.message : error || ""
      ).toLowerCase();
      if (
        message.includes("notallowed") ||
        message.includes("permission") ||
        message.includes("denied")
      ) {
        return "permission";
      }
      if (
        message.includes("model") ||
        message.includes("wasm") ||
        message.includes("backend") ||
        message.includes("fetch") ||
        message.includes("session")
      ) {
        return "detector";
      }
      return "detector";
    },
    handleAudioFrame(probabilities, frame) {
      const now = performance.now();
      this.speechProbability = probabilities.isSpeech || 0;
      if (this.speechStartedAt) {
        this.maxSpeechProbability = Math.max(
          this.maxSpeechProbability,
          this.speechProbability
        );
      }
      if (now - this.lastWaveDrawAt < 70) return;
      this.lastWaveDrawAt = now;

      let sum = 0;
      for (let i = 0; i < frame.length; i += 1) {
        sum += frame[i] * frame[i];
      }
      const rms = Math.sqrt(sum / Math.max(frame.length, 1));
      const level = Math.min(1, Math.max(0.025, rms * 8));
      this.waveSamples.push(level);
      if (this.waveSamples.length > 42) this.waveSamples.shift();
      this.drawWaveform();
    },
    drawWaveform() {
      const canvas = this.$refs.waveform;
      if (!canvas) return;
      const context = canvas.getContext("2d");
      const width = canvas.width;
      const height = canvas.height;
      context.clearRect(0, 0, width, height);

      const gap = 2;
      const barWidth = Math.max(2, width / this.waveSamples.length - gap);
      context.fillStyle =
        this.speechState === "speaking" ? "#e45b35" : "#4365d8";
      this.waveSamples.forEach((level, index) => {
        const barHeight = Math.max(2, level * (height - 6));
        const x = index * (barWidth + gap);
        const y = (height - barHeight) / 2;
        context.globalAlpha = 0.35 + level * 0.65;
        context.fillRect(x, y, barWidth, barHeight);
      });
      context.globalAlpha = 1;
    },
    handleSpeechStart() {
      this.maxSpeechProbability = this.speechProbability;
      this.speechStartedAt = this.logSpeech ? Date.now() : 0;
      this.speechConfirmed = false;
    },
    handleSpeechRealStart() {
      this.speechState = "speaking";
      this.speechConfirmed = true;
      if (this.logSpeech && !this.speechStartedAt) {
        this.speechStartedAt = Date.now();
      }
    },
    handleSpeechMisfire() {
      this.speechState = "quiet";
      this.speechStartedAt = 0;
      this.speechConfirmed = false;
      this.maxSpeechProbability = 0;
    },
    handleSpeechEnd() {
      this.finishActiveSpeechIncident(Date.now());
    },
    finishActiveSpeechIncident(endedAt) {
      const startedAt = this.speechStartedAt;
      const confirmed = this.speechConfirmed;
      this.speechState = "quiet";
      this.speechStartedAt = 0;
      this.speechConfirmed = false;
      if (!this.logSpeech || !startedAt || !confirmed) {
        this.maxSpeechProbability = 0;
        return;
      }
      this.queueSpeechLog(startedAt, endedAt);
    },
    queueSpeechLog(startedAt, endedAt) {
      const durationMs = endedAt - startedAt;
      if (durationMs < MIN_SPEECH_LOG_MS) {
        this.maxSpeechProbability = 0;
        return;
      }

      this.speechEventSequence += 1;
      const randomSuffix = Math.random().toString(36).slice(2, 10);
      this.speechLogQueue.push({
        event_type: "speech",
        student_id: getUserId(),
        project_code: this.resolvedProjectCode,
        template_id: this.templateId,
        start_time_ms: startedAt,
        end_time_ms: endedAt,
        duration_ms: durationMs,
        confidence: Number(this.maxSpeechProbability.toFixed(4)),
        client_timezone_offset: new Date().getTimezoneOffset(),
        client_event_id: `speech-${startedAt}-${this.speechEventSequence}-${randomSuffix}`,
        detector_version: "silero-v5-browser",
        _attempts: 0,
      });
      this.maxSpeechProbability = 0;
      this.flushSpeechLogs();
    },
    flushSpeechLogs() {
      if (
        this.speechLogSending ||
        this.speechLogQueue.length === 0 ||
        navigator.onLine === false
      ) {
        return;
      }

      const event = this.speechLogQueue[0];
      event._attempts = (event._attempts || 0) + 1;
      const payload = { ...event };
      delete payload._attempts;
      this.speechLogSending = true;
      this.speechLogState = "saving";
      saveProctorEvent(payload, {
        timeout: 8000,
        skipNetworkErrorMessage: true,
      })
        .then((response) => {
          const result = response && response.data ? response.data : null;
          if (!result || result.code !== 0) {
            throw new Error(
              result && result.msg ? result.msg : "Speech event was rejected"
            );
          }
          this.emitPenaltySummary(result);
          this.finishSpeechLog(event, true);
        })
        .catch((error) => {
          console.warn("Speech event save failed", error);
          if (event._attempts >= 3) {
            this.finishSpeechLog(event, false);
          } else {
            this.speechLogSending = false;
            this.speechLogState = "error";
            window.setTimeout(() => this.flushSpeechLogs(), 700);
          }
        });
    },
    finishSpeechLog(event, saved) {
      if (
        this.speechLogQueue[0] &&
        this.speechLogQueue[0].client_event_id === event.client_event_id
      ) {
        this.speechLogQueue.shift();
      }
      this.speechLogSending = false;
      if (saved) this.speechSavedCount += 1;
      this.speechLogState = saved ? "idle" : "error";
      if (this.speechLogQueue.length > 0) this.flushSpeechLogs();
    },
    emitPenaltySummary(result) {
      const summary = result && result.data ? result.data.summary : null;
      if (summary) {
        this.$emit("penalty-summary", summary);
      }
    },
    flushPenaltyEvents() {
      const now = Date.now();
      this.finishActiveSpeechIncident(now);
      this.flushFaceLogs();
      this.flushSpeechLogs();

      return new Promise((resolve) => {
        const startedAt = Date.now();
        const check = () => {
          const idle =
            !this.faceLogSending &&
            this.faceLogQueue.length === 0 &&
            !this.speechLogSending &&
            this.speechLogQueue.length === 0;
          if (idle) {
            resolve(true);
            return;
          }
          if (Date.now() - startedAt >= 9000) {
            resolve(false);
            return;
          }
          window.setTimeout(check, 60);
        };
        check();
      });
    },
    async destroyVad() {
      if (!this.vad) return;
      const vad = this.vad;
      this.vad = null;
      try {
        await vad.destroy();
      } catch (error) {
        console.warn("Unable to release VAD cleanly", error);
      }
    },
    async stopMonitoring(resetState = true) {
      this.finishFaceMissingIncident(Date.now());
      this.finishActiveSpeechIncident(Date.now());
      if (this.faceTimer) {
        window.clearTimeout(this.faceTimer);
        this.faceTimer = null;
      }
      if (this.faceDetector) {
        try {
          this.faceDetector.close();
        } catch (error) {
          console.warn("Unable to release face detector cleanly", error);
        }
        this.faceDetector = null;
      }
      await this.destroyVad();
      if (resetState) this.resetMonitorState();
    },
  },
};
</script>

<style lang="scss" scoped>
.proctor-monitor {
  --monitor-ink: #17203d;
  --monitor-blue: #4365d8;
  --monitor-orange: #e45b35;
  --monitor-green: #238767;
  display: grid;
  grid-template-columns: minmax(150px, 0.78fr) minmax(230px, 1.22fr);
  min-width: 0;
  overflow: hidden;
  color: var(--monitor-ink);
  background: #f7f8fc;
  border: 1px solid rgba(23, 32, 61, 0.14);
  box-shadow: 0 14px 36px rgba(23, 32, 61, 0.11);
}

.proctor-monitor__camera {
  position: relative;
  min-height: 150px;
  overflow: hidden;
  background: #11182a;
}

.proctor-monitor__video {
  width: 100%;
  height: 100%;
  min-height: 150px;
  display: block;
  object-fit: cover;
  transform: scaleX(-1);
}

.proctor-monitor__face-guide {
  position: absolute;
  z-index: 1;
  top: 12%;
  right: 25%;
  bottom: 10%;
  left: 25%;
  pointer-events: none;
  border: 2px solid rgba(255, 255, 255, 0.78);
  border-radius: 48% 48% 44% 44% / 42% 42% 54% 54%;
  box-shadow:
    0 0 0 999px rgba(10, 16, 31, 0.12),
    0 0 18px rgba(10, 16, 31, 0.28);
  transition: border-color 160ms ease, box-shadow 160ms ease;
}

.proctor-monitor--ok .proctor-monitor__face-guide {
  border-color: rgba(83, 226, 171, 0.96);
  box-shadow:
    0 0 0 999px rgba(10, 16, 31, 0.08),
    0 0 20px rgba(35, 135, 103, 0.4);
}

.proctor-monitor__live {
  position: absolute;
  top: 9px;
  left: 9px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 7px;
  color: #f8f9fd;
  background: rgba(15, 21, 37, 0.76);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.proctor-monitor__live-dot,
.proctor-monitor__recovery-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #ff7858;
}

.proctor-monitor__signals {
  min-width: 0;
  padding: 16px 18px 15px;
  text-align: left;
}

.proctor-monitor__heading {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  font-size: 14px;
  font-weight: 700;
}

.proctor-monitor__scope {
  color: #687087;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.proctor-monitor__waveform {
  width: 100%;
  height: 44px;
  display: block;
  margin: 10px 0 8px;
  background: #eef0f7;
}

.proctor-monitor__status-list {
  display: grid;
  gap: 7px;
}

.proctor-monitor__status {
  display: grid;
  grid-template-columns: 8px minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;
  min-width: 0;
  color: #4d566d;
  font-size: 12px;
  line-height: 1.25;
}

.proctor-monitor__status-copy {
  min-width: 0;
}

.proctor-monitor__status-copy small {
  display: block;
  margin-top: 3px;
  color: #8b3340;
  font-size: 9px;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.proctor-monitor__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #9ba2b5;
}

.proctor-monitor__dot.is-ok {
  background: var(--monitor-green);
}

.proctor-monitor__dot.is-warning {
  background: var(--monitor-orange);
}

.proctor-monitor__dot.is-error {
  background: #bd3344;
}

.proctor-monitor__dot.is-loading {
  background: #8b94aa;
  animation: proctor-pulse 1.4s ease-in-out infinite;
}

.proctor-monitor__metric {
  color: #747c90;
  font-size: 10px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.proctor-monitor--exam {
  grid-template-columns: 92px 188px;
  width: 280px;
  height: 68px;
  color: #f7f8fc;
  background: rgba(18, 27, 55, 0.92);
  border-color: rgba(255, 255, 255, 0.24);
  box-shadow: none;
}

.proctor-monitor--exam .proctor-monitor__camera,
.proctor-monitor--exam .proctor-monitor__video {
  min-height: 68px;
}

.proctor-monitor--exam .proctor-monitor__signals {
  padding: 6px 10px;
}

.proctor-monitor--exam .proctor-monitor__heading {
  color: #f7f8fc;
  font-size: 10px;
}

.proctor-monitor--exam .proctor-monitor__waveform {
  height: 14px;
  margin: 2px 0;
  background: rgba(255, 255, 255, 0.08);
}

.proctor-monitor--exam .proctor-monitor__status-list {
  gap: 3px;
}

.proctor-monitor--exam .proctor-monitor__status {
  color: #d9deec;
  font-size: 8px;
}

.proctor-monitor--exam .proctor-monitor__status-copy small {
  display: none;
}

.proctor-monitor--exam .proctor-monitor__status > span:nth-child(2),
.proctor-monitor--exam .proctor-monitor__status-copy,
.proctor-monitor--exam .proctor-monitor__status-copy > span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.proctor-monitor--exam .proctor-monitor__metric {
  color: #aeb7ce;
  font-size: 8px;
}

.proctor-monitor--exam .proctor-monitor__live {
  top: 5px;
  left: 5px;
  padding: 2px 5px;
  font-size: 8px;
}

@media (max-width: 1280px) {
  .proctor-monitor--exam {
    grid-template-columns: 80px 164px;
    width: 244px;
    height: 64px;
  }

  .proctor-monitor--exam .proctor-monitor__camera,
  .proctor-monitor--exam .proctor-monitor__video {
    min-height: 64px;
  }

  .proctor-monitor--exam .proctor-monitor__signals {
    padding: 5px 8px;
  }

  .proctor-monitor--exam .proctor-monitor__heading {
    font-size: 9px;
  }

  .proctor-monitor--exam .proctor-monitor__status {
    font-size: 8px;
  }
}

@media (max-width: 820px) {
  .proctor-monitor--exam {
    grid-template-columns: 68px 136px;
    width: 204px;
    height: 58px;
  }

  .proctor-monitor--exam .proctor-monitor__camera,
  .proctor-monitor--exam .proctor-monitor__video {
    min-height: 58px;
  }

  .proctor-monitor--exam .proctor-monitor__signals {
    padding: 4px 6px;
  }

  .proctor-monitor--exam .proctor-monitor__heading {
    font-size: 8px;
  }

  .proctor-monitor--exam .proctor-monitor__waveform {
    height: 10px;
  }

  .proctor-monitor--exam .proctor-monitor__status {
    gap: 4px;
    font-size: 7px;
  }
}

.proctor-monitor__blocker {
  position: fixed;
  z-index: 10020;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 24px;
  background: rgba(13, 18, 34, 0.9);
}

.proctor-monitor__blocker-panel {
  width: calc(100vw - 48px);
  max-width: 760px;
  display: grid;
  grid-template-columns: 70px minmax(0, 1fr) 220px;
  align-items: center;
  gap: 24px;
  padding: 28px;
  color: #17203d;
  background: #f7f8fc;
  border-top: 5px solid #e45b35;
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.34);
  text-align: left;
}

.proctor-monitor__blocker-mark {
  width: 64px;
  height: 64px;
  display: grid;
  place-items: center;
  color: #f7f8fc;
  background: #e45b35;
  font-size: 28px;
}

.proctor-monitor__eyebrow {
  margin-bottom: 6px;
  color: #bf482b;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.proctor-monitor__blocker h2 {
  margin: 0;
  color: #17203d;
  font-size: 25px;
  line-height: 1.15;
}

.proctor-monitor__blocker p {
  margin: 10px 0 14px;
  color: #525c73;
  font-size: 14px;
  line-height: 1.55;
}

.proctor-monitor__recovery {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #2d6f5a;
  font-size: 12px;
  font-weight: 700;
}

.proctor-monitor__recovery-dot {
  background: #238767;
  animation: proctor-pulse 1.4s ease-in-out infinite;
}

.proctor-monitor__blocker-video-wrap {
  position: relative;
  width: 220px;
  height: 165px;
  overflow: hidden;
  background: #11182a;
}

.proctor-monitor__blocker-video {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  transform: scaleX(-1);
}

@keyframes proctor-pulse {
  0%, 100% { opacity: 0.35; }
  50% { opacity: 1; }
}

@media (max-width: 760px) {
  .proctor-monitor:not(.proctor-monitor--exam) {
    grid-template-columns: 1fr;
  }

  .proctor-monitor__blocker-panel {
    grid-template-columns: 52px minmax(0, 1fr);
  }

  .proctor-monitor__blocker-mark {
    width: 48px;
    height: 48px;
  }

  .proctor-monitor__blocker-video-wrap {
    grid-column: 1 / -1;
    width: 100%;
  }
}
</style>
